import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Search, Users, ArrowRight, CheckCircle, MapPin, DollarSign, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import axios from 'axios';
import { API } from '../App';

export default function HomePage() {
  const [latestDeals, setLatestDeals] = useState([]);
  const [loadingDeals, setLoadingDeals] = useState(true);

  useEffect(() => {
    const fetchLatestDeals = async () => {
      try {
        const response = await axios.get(`${API}/deals`, {
          params: { limit: 3, sort: 'newest' }
        });
        // API returns deals as array directly
        setLatestDeals(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error fetching latest deals:', error);
      } finally {
        setLoadingDeals(false);
      }
    };
    fetchLatestDeals();
  }, []);

  const features = [
    {
      icon: Search,
      title: 'Find Off-Market Deals',
      description: 'Access exclusive investment properties before they hit the open market.'
    },
    {
      icon: Building2,
      title: 'Post Your Deals',
      description: 'Wholesalers and investors can list properties to our network of buyers.'
    },
    {
      icon: Users,
      title: 'Connect with Pros',
      description: 'Find investor-friendly realtors, lenders, contractors, and more.'
    }
  ];

  const stats = [
    { value: '500+', label: 'Active Deals' },
    { value: '200+', label: 'Service Providers' },
    { value: '10K+', label: 'Investors' },
    { value: '$50M+', label: 'Deal Volume' }
  ];

  const providerCategories = [
    'Realtors', 'Contractors', 'Hard Money Lenders', 'Private Lenders',
    'Title Companies', 'Inspectors', 'Property Managers', 'Attorneys'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/323781/pexels-photo-323781.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Luxury home"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <span className="overline text-[#5DADE2] mb-4 block" data-testid="hero-overline">
              Real Estate Investment Marketplace
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight mb-6" data-testid="hero-title">
              Your Next Flip Starts Here
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-8 max-w-xl">
              Discover off-market investment properties, connect with deal posters, and find trusted service providers—all in one platform built for serious investors.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="bg-[#5DADE2] hover:bg-[#4A9BD1] text-white rounded-full px-8" data-testid="browse-deals-btn">
                <Link to="/marketplace">
                  Browse Deals
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-full px-8" data-testid="post-deal-btn">
                <Link to="/post-deal">
                  Post a Deal
                </Link>
              </Button>
            </div>

            {/* Connect with a Pro CTA */}
            <div className="mt-8 pt-8 border-t border-white/20">
              <Link 
                to="/directory" 
                className="group flex items-center gap-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-2xl p-4 sm:p-5 transition-all"
                data-testid="connect-pro-cta"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center flex-shrink-0">
                  <Users className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-white text-base sm:text-lg">Connect with a Pro</span>
                    <ArrowRight className="h-4 w-4 text-[#5DADE2] group-hover:translate-x-1 transition-transform" />
                  </div>
                  <p className="text-sm text-slate-300 leading-snug">
                    Find investor-friendly agents, contractors, lenders, & more in our trusted directory
                  </p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${index}`}>
                <div className="font-heading text-4xl font-bold text-[#5DADE2] mb-2">{stat.value}</div>
                <div className="text-slate-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="overline text-[#5DADE2] mb-4 block">How It Works</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              Everything You Need to Invest
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="p-8 bg-white border border-slate-200 rounded-xl card-hover"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-14 h-14 rounded-xl bg-sky-50 flex items-center justify-center mb-6">
                  <feature.icon className="h-7 w-7 text-[#5DADE2]" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Deals Preview */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <span className="overline text-[#5DADE2] mb-4 block">Marketplace</span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
                Latest Off-Market Deals
              </h2>
            </div>
            <Button asChild variant="outline" className="mt-4 md:mt-0 rounded-full" data-testid="view-all-deals-btn">
              <Link to="/marketplace">
                View All Deals
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {loadingDeals ? (
              // Loading skeleton
              [1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-xl border border-slate-200 overflow-hidden animate-pulse">
                  <div className="aspect-[4/3] bg-slate-200"></div>
                  <div className="p-6">
                    <div className="h-4 bg-slate-200 rounded w-1/3 mb-3"></div>
                    <div className="h-6 bg-slate-200 rounded w-2/3 mb-4"></div>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="h-12 bg-slate-200 rounded"></div>
                      <div className="h-12 bg-slate-200 rounded"></div>
                    </div>
                    <div className="h-10 bg-slate-200 rounded-full"></div>
                  </div>
                </div>
              ))
            ) : latestDeals.length > 0 ? (
              latestDeals.map((deal) => {
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                const dealPhoto = deal.photos && deal.photos.length > 0 
                  ? (deal.photos[0].startsWith('http') ? deal.photos[0] : `${backendUrl}/api/files/${deal.photos[0]}`)
                  : 'https://images.pexels.com/photos/106399/pexels-photo-106399.jpeg?auto=compress&cs=tinysrgb&w=600';
                
                return (
                  <div key={deal.deal_id} className="bg-white rounded-xl border border-slate-200 overflow-hidden card-hover" data-testid={`preview-deal-${deal.deal_id}`}>
                    <div className="aspect-[4/3] bg-slate-200 relative">
                      <img
                        src={dealPhoto}
                        alt={deal.title || 'Property'}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'https://images.pexels.com/photos/106399/pexels-photo-106399.jpeg?auto=compress&cs=tinysrgb&w=600';
                        }}
                      />
                      <span className="absolute top-4 left-4 bg-[#5DADE2] text-white text-xs font-bold px-3 py-1 rounded-full">
                        OFF MARKET
                      </span>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-2 text-slate-500 text-sm mb-2">
                        <MapPin className="h-4 w-4" />
                        <span>{deal.city}, {deal.state}</span>
                      </div>
                      <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3 line-clamp-1">
                        {deal.title || `${deal.bedrooms || '?'}BR/${deal.bathrooms || '?'}BA ${deal.property_type || 'Property'}`}
                      </h3>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <div className="text-xs text-slate-500 mb-1">Asking Price</div>
                          <div className="font-semibold text-slate-900 flex items-center gap-1">
                            <DollarSign className="h-4 w-4" />
                            {deal.asking_price ? deal.asking_price.toLocaleString() : 'TBD'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-slate-500 mb-1">ARV</div>
                          <div className="font-semibold text-green-600 flex items-center gap-1">
                            <TrendingUp className="h-4 w-4" />
                            {deal.arv ? deal.arv.toLocaleString() : 'TBD'}
                          </div>
                        </div>
                      </div>
                      <Button asChild variant="outline" className="w-full rounded-full">
                        <Link to={`/deals/${deal.deal_id}`}>View Details</Link>
                      </Button>
                    </div>
                  </div>
                );
              })
            ) : (
              // No deals fallback
              <div className="col-span-3 text-center py-12">
                <p className="text-slate-500 mb-4">No deals available yet.</p>
                <Button asChild className="bg-[#5DADE2] hover:bg-[#4A9BD1] rounded-full">
                  <Link to="/post-deal">Post the First Deal</Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Service Providers CTA */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="overline text-[#5DADE2] mb-4 block">Service Directory</span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-6">
                Find Investor-Friendly Professionals
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Build your power team with vetted service providers who understand real estate investing. From hard money lenders to reliable contractors—we've got you covered.
              </p>
              
              <div className="flex flex-wrap gap-2 mb-8">
                {providerCategories.map((cat, i) => (
                  <span key={i} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-full text-sm font-medium">
                    {cat}
                  </span>
                ))}
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild className="bg-[#5DADE2] hover:bg-[#4A9BD1] rounded-full px-8" data-testid="browse-providers-btn">
                  <Link to="/directory">
                    Browse Directory
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="rounded-full px-8" data-testid="join-provider-btn">
                  <Link to="/signup?role=provider">
                    Join as a Provider — Free
                  </Link>
                </Button>
              </div>
            </div>
            
            <div className="relative max-w-md mx-auto lg:mx-0">
              <img
                src="https://images.pexels.com/photos/7641840/pexels-photo-7641840.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Real estate professional"
                className="rounded-2xl shadow-xl w-full max-h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-lg border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900 text-sm">Verified Providers</div>
                    <div className="text-xs text-slate-500">Trusted by investors</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight mb-6">
            Ready to Find Your Next Investment?
          </h2>
          <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
            Join thousands of investors who are finding profitable deals and building their teams on NextFlip.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-[#5DADE2] hover:bg-[#4A9BD1] rounded-full px-8" data-testid="get-started-btn">
              <Link to="/signup">
                Get Started Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-slate-600 text-white hover:bg-slate-800 rounded-full px-8">
              <Link to="/marketplace">
                Explore Marketplace
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
